import React from 'react';

const inputStyle = {
  width: '100%', padding: '0.5rem 0.625rem', borderRadius: 8,
  border: '1px solid #CBD5E1', background: '#FAF7F2',
  color: '#002A38', fontSize: '0.8125rem', outline: 'none', boxSizing: 'border-box'
};

const DEFAULT_WEIGHTS = { skills: 45, experience: 30, domain: 15, education: 10 };

// Toggle row with a label + description and a switch-like control.
function ToggleRow({ testid, label, desc, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
      <div>
        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#002A38' }}>{label}</div>
        <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.125rem', lineHeight: 1.4 }}>{desc}</div>
      </div>
      <button
        type="button"
        data-testid={testid}
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
        style={{
          flexShrink: 0, width: 42, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer',
          background: checked ? '#0076A3' : '#CBD5E1', position: 'relative', transition: 'background-color 0.2s'
        }}
      >
        <span style={{
          position: 'absolute', top: 2, left: checked ? 20 : 2, width: 20, height: 20, borderRadius: '50%',
          background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
        }} />
      </button>
    </div>
  );
}

// Advanced scoring controls for the JD↔Resume fitment engine.
// Operates on a flat form object; `setForm` must be the raw useState setter.
export function ScoringSettings({ form, setForm }) {
  const weights = form.score_weights || DEFAULT_WEIGHTS;
  const useCustom = !!form.score_weights;
  const total = (weights.skills || 0) + (weights.experience || 0) + (weights.domain || 0) + (weights.education || 0);

  const setWeight = (key, value) => {
    const v = Math.max(0, parseInt(value) || 0);
    setForm(prev => ({ ...prev, score_weights: { ...(prev.score_weights || DEFAULT_WEIGHTS), [key]: v } }));
  };

  const toggleCustom = (on) => {
    setForm(prev => ({ ...prev, score_weights: on ? { ...DEFAULT_WEIGHTS } : null }));
  };

  return (
    <div style={{ borderTop: '1px solid #ECE7DE', paddingTop: '1rem' }} data-testid="scoring-settings">
      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.25rem' }}>Scoring Settings</label>
      <p style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.875rem' }}>
        Tune how candidates are ranked against this job. Defaults work well for most roles.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        <ToggleRow
          testid="scoring-bias-blind-toggle"
          label="Bias-blind scoring"
          desc="Redact names, contact info and gendered language before scoring, so ranking is based on merit only."
          checked={!!form.bias_blind_scoring}
          onChange={(v) => setForm(prev => ({ ...prev, bias_blind_scoring: v }))}
        />
        <ToggleRow
          testid="scoring-high-accuracy-toggle"
          label="High-accuracy mode"
          desc="Credits transferable/adjacent skills and averages multiple AI passes for more stable, accurate scores. Uses more credits and is slightly slower."
          checked={!!form.high_accuracy_scoring}
          onChange={(v) => setForm(prev => ({ ...prev, high_accuracy_scoring: v }))}
        />
        <ToggleRow
          testid="scoring-custom-weights-toggle"
          label="Custom score weights"
          desc="Override how much skills, experience, domain and education each contribute to the overall score."
          checked={useCustom}
          onChange={toggleCustom}
        />

        {useCustom && (
          <div data-testid="scoring-weights" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.625rem', paddingTop: '0.25rem' }}>
            {[['skills', 'Skills'], ['experience', 'Experience'], ['domain', 'Domain'], ['education', 'Education']].map(([key, label]) => (
              <div key={key}>
                <label style={{ display: 'block', fontSize: '0.625rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>{label}</label>
                <input
                  data-testid={`weight-${key}-input`}
                  type="number" min="0" max="100"
                  value={weights[key] ?? 0}
                  onChange={(e) => setWeight(key, e.target.value)}
                  style={inputStyle}
                />
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1', fontSize: '0.625rem', color: total === 100 ? '#059669' : '#B45309' }}>
              Total: {total}{total !== 100 ? ' — values are auto-normalized to 100%' : '%'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
